import React from 'react';
import { StatusIcon, statusOptions, useHeightTransition } from '../Input';

interface TextareaProps extends React.HTMLProps<HTMLTextAreaElement> {
  maxLength?: number;
  currentLength?: number;
  id: string;
  label: string;
  description?: string;
  status?: keyof typeof statusOptions;
  errorMessage?: string;
}

const Textarea = React.forwardRef(function TextareaComponent(
  props: TextareaProps,
  ref: React.ForwardedRef<HTMLTextAreaElement>
) {
  const {
    id,
    label,
    description,
    maxLength,
    currentLength,
    errorMessage,
    status,
    ...textareaAttributes
  } = props;
  const showError = status === 'error' && typeof errorMessage !== 'undefined';
  const openAnimation = useHeightTransition(showError);

  const errorId = `${id}-error`;
  const descriptionId = `${id}-helper`;

  // ... associate error and helper text with the <input> [a11y support]
  const inputAriaDescribedBy: string[] = [];

  if (typeof description !== 'undefined') {
    inputAriaDescribedBy.push(descriptionId);
  }

  if (showError) {
    inputAriaDescribedBy.push(errorId);
  }

  return (
    <div className={`form-textarea-wrapper ${status ? statusOptions[status] : ''}`}>
      <div className="form-textarea-group">
        <textarea
          id={id}
          ref={ref}
          className="form-textarea-field"
          placeholder=" "
          maxLength={maxLength || undefined}
          aria-describedby={inputAriaDescribedBy.join(' ')}
          aria-invalid={showError}
          aria-required={textareaAttributes.required}
          {...textareaAttributes}
        />
        <label className="form-textarea-label" htmlFor={id}>
          {label}
        </label>
        {status && (
          <div className="absolute top-18 right-0">
            <StatusIcon status={status} />
          </div>
        )}
      </div>

      <div className="form-textarea-support">
        <div className="form-field-hints">
          {description && (
            <div className="form-field-helper" id={descriptionId}>
              {description}
            </div>
          )}
          <div {...openAnimation}>
            {showError && (
              <div
                className="form-field-error text-red"
                id={errorId}
                role="region"
                aria-live="polite"
                aria-atomic="true"
              >
                {errorMessage || 'error'}
              </div>
            )}
          </div>
        </div>
        {maxLength && typeof currentLength === 'number' && (
          <div className="form-field-count">
            <span>{currentLength}</span> / <span>{maxLength}</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default Textarea;
